/**
 * @module @complexcare/harmony
 * @file HxProgressBar Type Definitions
 */
import { HxControlContext, HxControlSize } from '../../base/types';

/** Visual presentation style of the progress indicator. */
export type ProgressBarVariant = 'linear' | 'circular';

/** Progress mode: determinate (known value) or indeterminate (loading). */
export type ProgressBarMode = 'determinate' | 'indeterminate';

/** Size variants for linear progress bars (height/thickness). */
export type ProgressBarLinearSize = Extract<HxControlSize, '6xs' | '5xs' | '4xs' | '3xs' | '2xs'>;

/** Size variants for circular progress spinners (diameter). */
export type ProgressBarCircularSize = Extract<HxControlSize, '2xs' | 'xs' | 'sm' | 'md' | 'lg'>;

/** Combined size type (variant-specific). */
export type ProgressBarSize = ProgressBarLinearSize | ProgressBarCircularSize;

/** Semantic color context following Moon Design System conventions. */
export type ProgressBarContext = HxControlContext;

/**
 * Defines a single segment in a multi-segment progress bar. Multiple segments allow visualizing progress across
 * multiple stages.
 *
 * @example
 *   ```typescript
 *   const segments: ProgressBarSegment[] = [
 *     { value: 30, label: 'Setup complete', context: 'positive' },
 *     { value: 20, label: 'Processing', context: 'brand' }
 *   ];
 *   ```;
 */
export type ProgressBarSegment = {
  /** Percentage value for this segment (0-100). Sum of all segments should not exceed 100. */
  value: number;

  /** Optional accessible label for screen readers and tooltips. */
  label?: string;

  /** Optional context override for this specific segment. If not provided, inherits from parent progress bar context. */
  context?: ProgressBarContext;
};

/** Event emitted when the progress value changes. */
export type ProgressBarValueChangeEvent = {
  /** New progress value (0-100). */
  value: number;

  /** Previous value before the change. */
  previousValue: number;

  /** Whether this change crossed a 10% announcement threshold. */
  crossedThreshold: boolean;
};

/** Event emitted when progress reaches 100%. */
export type ProgressBarCompleteEvent = {
  /** Final value (should be 100). */
  value: number;

  /** Timestamp when completion occurred. */
  timestamp: number;
};

/** Linear progress bar height mappings. */
export const LINEAR_SIZE_MAP: Record<ProgressBarLinearSize, number> = {
  '6xs': 2,
  '5xs': 4,
  '4xs': 8,
  '3xs': 12,
  '2xs': 16,
};

/** Circular progress spinner diameter mappings. */
export const CIRCULAR_SIZE_MAP: Record<ProgressBarCircularSize, number> = {
  '2xs': 16,
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
};

/** Circular progress spinner stroke width mappings (10% of diameter). */
export const CIRCULAR_STROKE_MAP: Record<ProgressBarCircularSize, number> = {
  '2xs': 2,
  xs: 2.4,
  sm: 3.2,
  md: 4,
  lg: 4.8,
};

/** Checks if a size value is valid for linear variant. */
export function isLinearSize(size: string): size is ProgressBarLinearSize {
  return ['6xs', '5xs', '4xs', '3xs', '2xs'].includes(size);
}

/** Checks if a size value is valid for circular variant. */
export function isCircularSize(size: string): size is ProgressBarCircularSize {
  return ['2xs', 'xs', 'sm', 'md', 'lg'].includes(size);
}

/** Clamps a number between 0 and 100. */
export function clampProgressValue(value: number): number {
  return Math.max(0, Math.min(100, value || 0));
}
